import CustodyScanForm, { type CustodyFormConfig, type CustodyRow } from './CustodyScanForm';
import { type AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDateTime } from '../../utils/datetime';

// Sample Disposal — scan a bottle currently In/Out (and past its sample-keeping
// window when one is set); it becomes a "Disposed"/"Depleted" custody row. "Dispose"
// action + a top-left "History" button. Prototype grid:
// SampleBottleID · DateDisposed · DisposedBy · Deplete · Hazardous · Mode · WasteDrumID
// — Deplete is an editable checkbox (ticking it flips Mode → Depleted, ALPACA
// SampleIn.OnSaving); Hazardous is a read-only checkbox from the sample's flag.
const buildColumns = ({ updateRow }: { updateRow: (id: string, patch: Partial<CustodyRow>) => void }): AppColumn<CustodyRow>[] => [
  { field: 'bottle',    header: 'SampleBottleID', width: 170 },
  { field: 'scannedAt', header: 'DateDisposed',   width: 160, text: r => fmtLocalDateTime(r.scannedAt) },
  { field: 'by',        header: 'DisposedBy',     width: 130 },
  { field: 'deplete',   header: 'Deplete', width: 90,
    render: r => (
      <input type="checkbox" checked={r.deplete}
        onClick={e => e.stopPropagation()}
        onChange={e => updateRow(r.id, { deplete: e.target.checked, mode: e.target.checked ? 'Depleted' : 'Disposed' })} />
    ),
    text: r => (r.deplete ? 'Yes' : 'No') },
  { field: 'haz',       header: 'Hazardous', width: 100,
    render: r => <input type="checkbox" checked={r.haz} disabled readOnly />,
    text: r => (r.haz ? 'Yes' : 'No') },
  { field: 'mode',      header: 'Mode', width: 110,
    render: r => <span className={`bl-pill ${r.mode === 'Depleted' ? 'warn' : 'alert'}`}>{r.mode}</span>, text: r => r.mode },
  { field: 'wasteDrum', header: 'WasteDrumID',    width: 130, text: r => r.wasteDrum || '—' },
];

const CONFIG: CustodyFormConfig = {
  pageId:         'sample-disposal',
  breadcrumbLeaf: 'Sample Disposal',
  subtitle:       'Scan bottle barcodes to dispose them',
  actionLabel:    'Dispose',
  confirmTitle:   'Dispose',
  confirmMessage: 'Do you want to dispose the scanned sample(s)? This action is irreversible.',
  successVerb:    'Disposed',
  formName:       'Sample Disposal',
  buildColumns,
  historyRoute:   '/m/sample-disposal-history',
  resolveUrl:     '/api/v1/sample-custody/resolve-disposal',
  saveUrl:        '/api/v1/sample-custody/sample-disposal',
  emptyPrompt:    'No data to display — scan a barcode above.',
  buildRow: (r, ctx) => ({
    id:             `${r.sampleBottleId}-${ctx.nowIso}-${Math.round(performance.now())}`,
    bottle:         r.sampleBottleId ?? '',
    from:           '',
    to:             '',
    scannedAt:      ctx.nowIso,
    by:             ctx.userName,
    haz:            !!r.hazardous,
    mode:           'Disposed',
    deplete:        false,
    wasteDrum:      '',
    sampleLoginOid: r.sampleLoginOid ?? null,
    jobOid:         r.jobOid ?? null,
    client:         r.client ?? null,
  }),
  buildScan: (row) => ({
    sampleBottleId: row.bottle,
    sampleLoginOid: row.sampleLoginOid,
    deplete:        row.deplete,
    wasteDrumId:    row.wasteDrum || null,
  }),
};

export default function SampleDisposalPage() {
  return <CustodyScanForm config={CONFIG} />;
}
